import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsMyPropertyHotelDetailsController from 'lumen/controllers/hotel/settings/my-property/hotel-details';
import type { SubscriptionProfile } from 'lumen/models';
import type { SubscriptionProfilePayload } from 'lumen/models/subscription/profile';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { UNIFIED_SETTINGS_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import {
  geocodeAddress,
  isLegacySentinelCoords,
} from 'lumen/utils/google-maps/geocode-address';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

export type HotelDetailsModel = {
  geocodeProfileCoordsTask: Task<void, [SubscriptionProfile]>;
  requestProfileTask: Task<SubscriptionProfile, []>;
};

export type HotelDetailsFormModel = {
  geocodeProfileCoordsTask: Task<void, [SubscriptionProfile]>;
  onPendingLogoChange: (value: 'upload' | 'delete' | null) => void;
  pendingLogoChange: 'upload' | 'delete' | null;
  requestProfileTask: Task<SubscriptionProfile, []>;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: [
    UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_VIEW,
    UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_EDIT,
  ],
  redirectTo: 'hotel.settings',
})
export default class HotelSettingsMyPropertyHotelDetailsRoute extends Route {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  #transitionHandler?: TransitionHandler;

  requestProfileTask: Task<SubscriptionProfile, []> = task(async () => {
    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id!;

      const url = formatUrl(
        `/${APIGATEWAY}/configure/subscription/profile`,
        { subscription_id: subscriptionId },
        this.ajax.host,
      );
      const response = await this.ajax.request<SubscriptionProfilePayload>(
        url,
        { method: 'GET' },
      );
      const result = pushAndReturnPayload(
        this.store,
        { 'subscription-profiles': [{ ...response, id: subscriptionId }] },
        'subscription/profile',
      );

      const profile = (
        result['subscription-profiles'] as SubscriptionProfile[]
      )[0]!;

      // (0, 0) is the sentinel value for "no real coords stored" (legacy Stardekk data).
      // Kick off background geocoding so the map pin and Save button are ready
      // without any user interaction. Out-of-range values (e.g. 300, 0) are handled
      // by the component's overlay + Refresh button flow instead.
      void this.geocodeProfileCoordsTask.perform(profile);

      return profile;
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });
  /**
   * Profiles stored with (0, 0) have no usable coords due to legacy data issues.
   * This task geocodes the address and writes the recovered values back onto the
   * model so the form becomes save-ready. The write is guarded: if the user edits
   * the form before geocoding finishes (clearing lat/lng to null), the stale result
   * is discarded rather than overwriting user input.
   */
  geocodeProfileCoordsTask = task(
    async (profile: SubscriptionProfile): Promise<void> => {
      if (!isLegacySentinelCoords(profile.latitude, profile.longitude)) {
        return;
      }
      const coords = await geocodeAddress(profile.address);
      if (!coords) {
        return;
      }
      profile.latitude = coords.lat;
      profile.longitude = coords.lng;
    },
  );

  deactivate(): void {
    this.#transitionHandler?.cleanup();
    this.#transitionHandler = undefined;

    void this.requestProfileTask.cancelAll();
    void this.geocodeProfileCoordsTask.cancelAll();

    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('subscription/profile');
    this.store.unloadAll('subscription/channel-distribution');
  }

  model(): HotelDetailsModel {
    void this.requestProfileTask.perform();
    return {
      geocodeProfileCoordsTask: this.geocodeProfileCoordsTask,
      requestProfileTask: this.requestProfileTask,
    };
  }

  resetController(
    controller: HotelSettingsMyPropertyHotelDetailsController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      controller.pendingLogoChange = null;

      if (this.#transitionHandler) {
        this.#transitionHandler.cleanup();
        this.#transitionHandler = undefined;
      }
    }
  }

  setupController(
    controller: HotelSettingsMyPropertyHotelDetailsController,
    model: HotelDetailsModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    this.#transitionHandler?.cleanup();
    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'hotel.settings.my-property.hotel-details',
    );
  }
}
